import { t, jt } from 'ttag'
import { useContext } from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'

import { Modal } from '@h2/components/containers/modal'
import { Flex } from '@h2/components/layout/flex'
import { Heading } from '@h2/components/typography/heading'
import { Text } from '@h2/components/typography/text'
import { Switch } from '@h2/components/inputs/switch'
import { InfoCircleIcon } from '@h2/components/icons/infoCircle'
import { SDB, PB97T } from '@h2/components/colors'
import { features } from '@h2/components/media'

import { Context } from './context'

const StyledFlex = styled(Flex)`
  @media (${features.minWidth.laptop}) {
    flex-direction: row;
  }
`
const Header = styled.header`
  display: flex;
  align-items: center;
`
const Article = styled.article`
  &:first-child {
    margin: 0 0 25px 0;
  }

  div {
    border: 1px solid ${PB97T};
    border-radius: 5px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.16);
    padding: 15px 0;
    text-align: center;
    margin-bottom: 20px;

    .MuiSwitch-switchBase {
      cursor: default;
    }
  }

  @media (${features.minWidth.laptop}) {
    &:first-child {
      margin: 0 40px 0 0;
    }
  }
`
const ViewSwitcherModal = ({ onClose }) => {
  const { system } = useContext(Context)
  const systemName = <strong key="system">{system}</strong>

  return (
    <Modal isOpen={true} onClose={onClose} hasBorders={false}>
      <Modal.Header>
        <Header>
          <InfoCircleIcon size="large" />
          <Heading mb="0" ml="15px" level="2">
            {t`View modes`}
          </Heading>
        </Header>
      </Modal.Header>
      <Modal.Body>
        <Text size="medium">
          {jt`The ${systemName} provides two different view modes to help educators get the most out of their Headed2 account. You can toggle between explorer mode, where you can see your individual progress (as a student would see things), and navigator mode where you can view dashboards summarizing the progress of an entire class of students.`}
        </Text>
        <StyledFlex direction="column">
          <Article>
            <Heading level="4">{t`Explorer Mode`}</Heading>
            <div>
              <Switch
                labelPosition="both"
                label={[t`Navigator Mode`, t`Explorer Mode`]}
                isOn={true}
              />
            </div>
            <Text size="medium">
              {t`In this view, you will see the system as a normal user would. From here you can complete various activities, see recommendations, and continue your work on assignments and plans. As you navigate through the system, your options for interacting with recommendations and profiles will be the same as if you were a student.`}
            </Text>
          </Article>
          <Article>
            <Heading level="4">{t`Navigator Mode`}</Heading>
            <div>
              <Switch
                offColor={SDB}
                labelPosition="both"
                label={[t`Navigator Mode`, t`Explorer Mode`]}
                isOn={false}
              />
            </div>
            <Text size="medium">
              {t`In this view, you will have a higher level view of the work of all the users you are managing. The dashboard provides at-a-glance statistics on student’s progress with assignments, usage, and interactions with the local opportunities network. As you navigate through the system, the options for interacting with profiles and content will allow you to recommend content to users.`}
            </Text>
          </Article>
        </StyledFlex>
      </Modal.Body>
    </Modal>
  )
}

ViewSwitcherModal.propTypes = {
  onClose: PropTypes.func.isRequired
}

export { ViewSwitcherModal }
